import { useEffect, useMemo, useRef, useState } from "react";

import {
  getVerificationFieldCapabilities,
  type VerificationFieldCapability,
} from "../lib/verification-api";
import type { ProfileRowId } from "../lib/readiness/factor-routing";

type TokenResolver = (options?: {
  skipCache?: boolean;
}) => Promise<string | null>;

/**
 * Per-field verification capability lookup. Every method FAILS CLOSED —
 * an unresolved table, a failed read, and a field the server never
 * mentioned all report `false` with no reason.
 *
 * Closed is the correct default here because the failure this replaces was
 * an over-offer, not an under-offer: the passport painted "Upload document"
 * on every row, including a six-value enum no document can evidence. A
 * momentarily-missing button is a smaller harm than a button that accepts a
 * file and changes nothing.
 */
export interface VerificationFieldCapabilityLookup {
  supportsBank: (rowId: ProfileRowId) => boolean;
  supportsDocument: (rowId: ProfileRowId) => boolean;
  /** Buyer-facing "why not", rendered verbatim. Undefined when supported. */
  bankReason: (rowId: ProfileRowId) => string | undefined;
  documentReason: (rowId: ProfileRowId) => string | undefined;
  isLoading: boolean;
}

/**
 * Session-scoped cache. The capability table is a statement about what Sill
 * can parse, not about this buyer — it cannot change while the tab is open,
 * so one fetch serves every passport row, the Edit & Verify drawer, and any
 * remount. Caching the PROMISE (not just the value) also collapses the
 * concurrent first-mount case: two components mounting in the same tick
 * await the same request rather than racing two.
 */
let cachedTable: Promise<VerificationFieldCapability[] | null> | null = null;

/** Test seam — resets the module cache between cases. Never called by app code. */
export function __resetVerificationFieldCapabilitiesCache() {
  cachedTable = null;
}

/**
 * The fail-closed lookup: nothing is verifiable, no reasons. What the hook
 * reports before the table resolves and after a failed read.
 *
 * Exported so render-only tests and non-authenticated surfaces state this
 * default explicitly instead of getting it from an absent prop — a silently
 * capability-less passport is indistinguishable from a broken one.
 */
export const FAIL_CLOSED_CAPABILITIES: VerificationFieldCapabilityLookup = {
  supportsBank: () => false,
  supportsDocument: () => false,
  bankReason: () => undefined,
  documentReason: () => undefined,
  isLoading: false,
};

function loadTable(
  getToken: TokenResolver,
): Promise<VerificationFieldCapability[] | null> {
  // Deliberately does NOT clear the cache on a null result. A failed read
  // already fails closed, and re-requesting on every mount would turn a
  // backend blip into a request storm from a section that remounts freely.
  cachedTable ??= getVerificationFieldCapabilities(getToken);
  return cachedTable;
}

/**
 * Reads the server-owned capability table (task 1678) and exposes a
 * fail-closed lookup over it.
 *
 * This replaces two frontend decisions: the hardcoded BANK_VERIFIABLE_ROWS
 * set, and the complete ABSENCE of a document-upload decision. Adding a
 * field, or teaching Sill to parse a new document, is now a backend change
 * plus a row in TestFieldCapabilities — nothing here moves.
 */
export function useVerificationFieldCapabilities(
  getToken: TokenResolver | undefined,
): VerificationFieldCapabilityLookup {
  const [byField, setByField] = useState<Map<
    string,
    VerificationFieldCapability
  > | null>(null);

  // Mirrors useVerificationPath's getTokenRef idiom: hold the latest resolver
  // in a ref (updated in its own Effect, never during render) so the fetch
  // Effect can run exactly once instead of re-firing on every getToken
  // identity change.
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const authed = getToken !== undefined;

  useEffect(() => {
    // No session, no fetch. The route sits behind the auth perimeter, so
    // calling it unauthenticated buys a guaranteed 401 and then poisons the
    // module cache with that failure for the rest of the tab. Preview and
    // signed-out surfaces stay fail-closed, which is what they render anyway.
    if (!authed) return;
    let cancelled = false;
    void (async () => {
      const resolver = getTokenRef.current;
      if (!resolver) return;
      const table = await loadTable(resolver);
      if (cancelled) return;
      setByField(table ? new Map(table.map((c) => [c.field, c])) : new Map());
    })();
    return () => {
      cancelled = true;
    };
  }, [authed]);

  // One memo on the RETURNED OBJECT rather than a useCallback per member.
  // Memoizing the members was pure churn: the hook handed back a fresh object
  // literal every render, so no consumer could ever observe the stable
  // identities. This is the identity that actually travels — InputsColumn
  // receives it as a prop.
  return useMemo(() => {
    const lookup = (rowId: ProfileRowId) => byField?.get(rowId);
    return {
      supportsBank: (rowId) => lookup(rowId)?.supports_bank ?? false,
      supportsDocument: (rowId) => lookup(rowId)?.supports_document ?? false,
      bankReason: (rowId) => lookup(rowId)?.bank_reason,
      documentReason: (rowId) => lookup(rowId)?.document_reason,
      // Derived, not a second useState kept in lockstep with byField.
      isLoading: byField === null && authed,
    };
  }, [authed, byField]);
}
